import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { Label } from "@/components/ui/label";
import { ExclamationTriangleIcon } from "@radix-ui/react-icons";
import { ServiceType, OrganizationQuery, OrganizationQueryVariables, OrganizationDocument } from "@/graphql/__generated__/graphql";
import { convertBpToDecimal } from "@/utils/helpers";
import { TFunction } from "i18next";
import { IntlShape } from "react-intl";
import { Card } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import { InfoIcon } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cn } from "@/lib/utils";
import { useQuery } from "@apollo/client/react";

interface IFormInput {
  name: string;
  label: string;
  value: number;
  useFormattedInput?: boolean;
  isDifferent?: boolean;
}

/** Base interface for Advanced Cost Parameters */
interface BaseACP {
  averageHoursWorkedPerMonth: number;
  trainingDays: number;
  yearlyTrainingCosts: number;
  sickDays: number;
  employeeFluctuation: number;
  yearlyUniformCosts: number;
  otherMaterialCosts: number;
  averageLengthOfEmployment: number;
  carCostsPerMinute?: number;
  otherCostsPerMinute?: number;
}

/** Props interface for the ACP component */
interface AdvancedCostParametersSectionProps {
  acp: BaseACP;
  organizationId: number;
  serviceType: ServiceType;
}

/** Interface for organization's ACP where all fields are optional */
type OrganizationACP = Partial<BaseACP>;

/**
 * createACPDataObject - This function creates the ACP object array
 * @param {TFunction} t - The translation function
 * @param {AdvancedCostParametersSectionProps["acp"]} acp - The ACP component props
 * @param {OrganizationACP} organizationValue - The organization ACP value
 * @param {ServiceType} serviceType - The service type
 * @returns {IFormInput[]} - The ACP data object
 */
const createACPDataObject = (t: TFunction, acp: AdvancedCostParametersSectionProps["acp"], organizationValue: OrganizationACP, serviceType: ServiceType): IFormInput[] => {
  const compareValue = (key: keyof BaseACP) => {
    return Number(acp?.[key]) !== Number(organizationValue?.[key]);
  };

  return [
    {
      name: "averageHoursWorkedPerMonth",
      label: t("edittender.calculationoverview.components.acp.labels.averagehoursworkedpermonth"),
      value: acp.averageHoursWorkedPerMonth,
      isDifferent: compareValue("averageHoursWorkedPerMonth")
    },
    {
      name: "trainingDays",
      label: t("edittender.calculationoverview.components.acp.labels.trainingdays"),
      value: acp.trainingDays,
      isDifferent: compareValue("trainingDays")
    },
    {
      name: "yearlyTrainingCosts",
      label: t("edittender.calculationoverview.components.acp.labels.yearlytrainingcosts"),
      value: acp.yearlyTrainingCosts,
      useFormattedInput: true,
      isDifferent: compareValue("yearlyTrainingCosts")
    },
    {
      name: "sickDays",
      label: t("edittender.calculationoverview.components.acp.labels.sickdays"),
      value: acp.sickDays,
      isDifferent: compareValue("sickDays")
    },
    {
      name: "employeeFluctuation",
      label: t("edittender.calculationoverview.components.acp.labels.employeefluctuation"),
      value: convertBpToDecimal(acp.employeeFluctuation),
      isDifferent: compareValue("employeeFluctuation")
    },
    {
      name: "yearlyUniformCosts",
      label: t("edittender.calculationoverview.components.acp.labels.yearlyuniformcosts"),
      value: acp.yearlyUniformCosts,
      useFormattedInput: true,
      isDifferent: compareValue("yearlyUniformCosts")
    },
    {
      name: "otherMaterialCosts",
      label: t("edittender.calculationoverview.components.acp.labels.othermaterialcosts"),
      value: acp.otherMaterialCosts,
      useFormattedInput: true,
      isDifferent: compareValue("otherMaterialCosts")
    },
    {
      name: "averageLengthOfEmployment",
      label: t("edittender.calculationoverview.components.acp.labels.averagelengthofemployment"),
      value: acp.averageLengthOfEmployment,
      isDifferent: compareValue("averageLengthOfEmployment")
    },
    ...(serviceType === ServiceType.Mobile && acp.carCostsPerMinute !== undefined ? [{
      name: "carCostsPerMinute",
      label: t("edittender.calculationoverview.components.acp.labels.carcostsperminute"),
      value: acp.carCostsPerMinute,
      useFormattedInput: true,
      isDifferent: compareValue("carCostsPerMinute")
    }] : []),
    ...(serviceType === ServiceType.Mobile && acp.otherCostsPerMinute !== undefined ? [{
      name: "otherCostsPerMinute",
      label: t("edittender.calculationoverview.components.acp.labels.othercostsperminute"),
      value: acp.otherCostsPerMinute,
      useFormattedInput: true,
      isDifferent: compareValue("otherCostsPerMinute")
    }] : [])
  ];
};

/**
 * renderInputs - This function renders the ACP form inputs
 * @param {IntlShape} intl - The intl object
 * @param {TFunction} t - The translation function
 * @param {IFormInput[]} inputs - The ACP form inputs
 * @param {OrganizationACP} organizationValue - The organization ACP value
 */
const renderInputs = (intl: IntlShape, t: TFunction, inputs: IFormInput[], organizationValue: OrganizationACP) => {
  return inputs.map((input) => {
    const orgValue = organizationValue[input.name as keyof OrganizationACP];
    const numericOrgValue = typeof orgValue === "number" ? orgValue : undefined;

    return (
      <div key={`acp-display-${input.name}`} className="flex flex-col gap-2 w-full">
        <div className="flex flex-wrap gap-1 items-center justify-between rounded-md p-2 hover:bg-slate-50">
          <div>
            <Label className={cn("flex items-center", input.isDifferent ? "text-orange-500" : "text-slate-500")}>
              {input.isDifferent && (
                <ExclamationTriangleIcon className="inline h-4 mr-1 text-orange-500" />
              )}
              {input.label}
            </Label>
          </div>
          <div className="flex gap-2 items-center justify-end">
            <div>
              {input.useFormattedInput
                ? intl.formatNumber(input.value, { style: "currency", currency: "EUR" })
                : input.value}
            </div>
            {
              numericOrgValue && (
                <div className="flex items-center text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded-md">
                  {input.name === "employeeFluctuation" ? convertBpToDecimal(numericOrgValue) : numericOrgValue}
                  <HoverCard>
                    <HoverCardTrigger asChild>
                      <Button variant="ghost" size="icon" className="ml-1 p-0 h-fit w-fit hover:cursor-help" type="button">
                        <InfoIcon className="inline h-4 text-slate-400" />
                      </Button>
                    </HoverCardTrigger>
                    <HoverCardContent side="left" className="max-w-[180px]">
                      <p className="text-xs">
                        {t("modals:editpermanentservicemodal.defaultvaluetooltip", { label: input.label })}
                      </p>
                    </HoverCardContent>
                  </HoverCard>
                </div>
              )
            }
          </div>
        </div>
      </div>
    );
  });
};

/**
 * AdvancedCostParametersSection - This component renders the ACP component
 * @param {AdvancedCostParametersSectionProps} acp - The ACP component props
 * @param {number} organizationId - The organization id
 * @param {ServiceType} serviceType - The service type
 * @returns {JSX.Element} - The ACP component
 */
function AdvancedCostParametersSection({ acp, organizationId, serviceType }: AdvancedCostParametersSectionProps) {
  const { t } = useTranslation(["translation", "common", "modals"]);
  const intl = useIntl();

  const { data: organizationData } = useQuery<OrganizationQuery, OrganizationQueryVariables>(OrganizationDocument, {
    variables: {
      organizationId: Number(organizationId)
    },
    skip: !organizationId
  });
  const organization = organizationData?.organization;

  const ACPData = createACPDataObject(t, acp, organization?.advancedCostParameters || {}, serviceType);
  const containDifferences = ACPData.some((input) => input.isDifferent);

  return (
    <Card className="shadow-none">
      <Accordion type="single" collapsible>
        <AccordionItem value="acp">
          <AccordionTrigger className="hover:no-underline hover:bg-slate-50 px-4 py-4">
            <div className="flex flex-col gap-1 items-start">
              <div className="text-sm font-bold">{t("edittender.calculationoverview.components.acp.title")}</div>
              {containDifferences && (
                <div>
                  <div className="flex items-center text-left gap-2 text-xs text-orange-500">
                    <ExclamationTriangleIcon className="inline h-4 mr-1 shrink-0" />
                    <p>{t("modals:editpermanentservicemodal.differentvalues")}</p>
                  </div>
                </div>
              )}
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pt-2 pb-4">
            <div className="flex-col-4">
              <div className="flex-wrap-2">
                {renderInputs(intl, t, ACPData, organization?.advancedCostParameters || {})}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}

export default AdvancedCostParametersSection;
